import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';

import { Timestamp } from 'firebase/firestore';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastrService } from 'ngx-toastr';

import { Question } from '../firestore-models/question';
import { ProjectLanguage } from '../firestore-models/project-language';
import { ApiQuestion, ApiSetQuestionsRequest } from '../api-client-module';
import { ApiSetQuestSetRequest } from '../api-client-module';

import { ApiClientService } from '../api-client.service';
import { ConfigurationService } from '../configuration.service';
import { SpinnerService } from '../services/spinner.service';
import { AppConst } from '../app-const';

import { QuestionDialogComponent } from './question-dialog/question-dialog.component';
import { QuestionSortDialogComponent } from './question-sort-dialog/question-sort-dialog.component';
import { MessageDialogComponent } from '../common/message-dialog/message-dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-questions',
  templateUrl: './questions.component.html',
  styleUrls: ['./questions.component.css'],
  standalone: false,
})
export class QuestionsComponent implements OnInit {

  //Questionコレクション
  private questionsCollection!: AngularFirestoreCollection<Question>;
  questionsObservable!: Observable<Question[]>;
  questionsSubscribe!: Subscription;
  //各設問情報に紐付くプロジェクトID
  projectId: string;
  //各設問情報に紐付くプロジェクトの言語情報
  projectLanguages!: Array<ProjectLanguage>;

  // 設問情報テーブル
  displayedColumns: string[] = ['position', 'edit', 'select', 'question_id', 'question_name', 'question_type', 'questions', 'display_no'];
  dataSource = new MatTableDataSource<Question>();
  selection = new SelectionModel<Question>(true, []);

  uploadedFilesCollectionName = AppConst.FIRESTORE_UPLOADED_FILES;

  projectBaseLang: string = 'ja';

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
    this.projectId = this.configurationService.getProjectId();
  }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.questionsCollection = this.angularFirestore.collection<Question>(
      AppConst.FIRESTORE_QUESTIONS, ref => ref.where('project_id', '==', this.projectId).orderBy('display_no', 'asc'));
    this.questionsObservable = this.questionsCollection.valueChanges({ idField: 'id' });
    this.questionsSubscribe = this.questionsObservable.subscribe(questions => {
      this.dataSource.data = this.addFilterValue(questions);
    });
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
  }

  ngOnDestroy(): void {
    this.questionsSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: Question): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  //編集ダイアログ
  openEditDialog(question: Question): void {
    const question_check_date_range_upper: any = question.question_check_date_range_upper ? new Date(question!.question_check_date_range_upper.seconds * 1000) : null;
    const question_check_date_range_lower: any = question.question_check_date_range_lower ? new Date(question!.question_check_date_range_lower.seconds * 1000) : null;
    const question_check_base_date: any = question.question_check_base_date ? new Date(question!.question_check_base_date.seconds * 1000) : null;

    let apiQuestion: ApiQuestion = {
      question_id: question.question_id,
      display_no: question.display_no,
      question_names: question.question_names,
      question_comps: question.question_comps,
      question_type: question.question_type,
      question_select_items: question.question_select_items,
      question_check: question.question_check,
      question_check_range_upper: question.question_check_range_upper,
      question_check_range_lower: question.question_check_range_lower,
      question_check_date_range_upper: question_check_date_range_upper,
      question_check_date_range_lower: question_check_date_range_lower,
      question_check_base_date: question_check_base_date,
      question_check_base_date_to_start: question.question_check_base_date_to_start,
      question_check_base_date_to_end: question.question_check_base_date_to_end,
      question_check_col_count_upper: question.question_check_col_count_upper,
      question_check_col_count_lower: question.question_check_col_count_lower,
      project_id: question.project_id,
      enabled: question.enabled,
    };
    const dialogRef = this.dialog.open(QuestionDialogComponent, {
      height: '85%',
      width: '85%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_UPDATE, question: apiQuestion }
    });
  }

  //追加ダイアログ
  openAddDialog(): void {
    const dialogRef = this.dialog.open(QuestionDialogComponent, {
      height: '85%',
      width: '85%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_ADD, question: null }
    });
  }

  //並べ替えダイアログ
  openSortDialog(): void {
    let apiQuestions: Array<ApiQuestion> = new Array<ApiQuestion>();

    for (const question of this.dataSource.data) {
      const question_check_date_range_upper: any = question.question_check_date_range_upper ? new Date(question!.question_check_date_range_upper.seconds * 1000) : null;
      const question_check_date_range_lower: any = question.question_check_date_range_lower ? new Date(question!.question_check_date_range_lower.seconds * 1000) : null;
      const question_check_base_date: any = question.question_check_base_date ? new Date(question!.question_check_base_date.seconds * 1000) : null;

      let apiQuestion: ApiQuestion = {
        question_id: question.question_id,
        display_no: question.display_no,
        question_names: question.question_names,
        question_comps: question.question_comps,
        question_type: question.question_type,
        question_select_items: question.question_select_items,
        question_check: question.question_check,
        question_check_range_upper: question.question_check_range_upper,
        question_check_range_lower: question.question_check_range_lower,
        question_check_date_range_upper: question_check_date_range_upper,
        question_check_date_range_lower: question_check_date_range_lower,
        question_check_base_date: question_check_base_date,
        question_check_base_date_to_start: question.question_check_base_date_to_start,
        question_check_base_date_to_end: question.question_check_base_date_to_end,
        question_check_col_count_upper: question.question_check_col_count_upper,
        question_check_col_count_lower: question.question_check_col_count_lower,
        project_id: question.project_id,
        enabled: question.enabled,
      };
      apiQuestions.push(apiQuestion);
    }

    const dialogRef = this.dialog.open(QuestionSortDialogComponent, {
      height: '85%',
      width: '85%',
      disableClose: true,
      data: { questions: apiQuestions }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        result.project_id = this.projectId;
        let request: ApiSetQuestionsRequest = {
          access_token: '',
          change_type: AppConst.DATA_CHANGE_TYPE_UPDATE,
          questions: result
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setQuestions(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('更新しました。'));
            } else {
              this.toastr.error(this.translate.instant('更新できませんでした。'), '', { timeOut : 5000 });
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('更新できませんでした。'), '', { timeOut : 5000 });
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
          }
        );
      }
    });
  }

  //削除
  deleteData(): void {
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      height: '180px',
      disableClose: true,
      data: { message: this.translate.instant('選択されたデータを削除します。よろしいですか？') }
    });

    dialogRef.afterClosed().subscribe(result => {
      let selectedQuestions: Array<ApiQuestion> = new Array<ApiQuestion>();
      for (const row of this.dataSource.data) {
        if (this.selection.isSelected(row)) {
          const selectedQuestion: ApiQuestion = {
            question_id: row.question_id,
            display_no: row.display_no,
            question_names: row.question_names,
            question_comps: row.question_comps,
            question_type: row.question_type,
            question_select_items: row.question_select_items,
            question_check: row.question_check,
            // question_check_range_upper: row.question_check_range_upper,
            // question_check_range_lower: row.question_check_range_lower,
            // question_check_date_range_upper: row.question_check_date_range_upper,
            // question_check_date_range_lower: row.question_check_date_range_lower,
            // question_check_base_date: row.question_check_base_date,
            // question_check_base_date_to_start: row.question_check_base_date_to_start,
            // question_check_base_date_to_end: row.question_check_base_date_to_end,
            // question_check_col_count_upper: row.question_check_col_count_upper,
            // question_check_col_count_lower: row.question_check_col_count_lower,
            project_id: row.project_id,
            enabled: row.enabled,
          };
          selectedQuestions.push(selectedQuestion);
        }
      }
      if (!!result) {
        let request: ApiSetQuestionsRequest = {
          access_token: '',
          change_type: AppConst.DATA_CHANGE_TYPE_DELETE,
          questions: selectedQuestions
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setQuestions(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('questions.pg.DELETE_SUCCESS'));
            } else {
              this.toastr.error(this.translate.instant('削除できませんでした。'));
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('questions.pg.DELETE_ERROR'));
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
            this.selection.clear();
          }
        );
      }
    });
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'question_id':
          return compare(a.question_id, b.question_id, isAsc);
        case 'display_no':
          return compare(a.display_no, b.display_no, isAsc);
        case 'question_name':
          const aName = a.question_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code })[0].name;
          const bName = b.question_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code })[0].name;
          return compare(aName, bName, isAsc);
        case 'question_type':
          return compare(a.question_type, b.question_type, isAsc);
        default:
          return 0;
      }
    });
  }

  addFilterValue(docs: Array<any>): Array<any> {
    for (const doc of docs) {
      let filterValue = '';
      for (const value of doc.question_names) {
        filterValue += value.name + ' '
      }
      for (const value of doc.question_comps) {
        filterValue += value.name + ' '
      }
      doc['filterValue'] = filterValue;
    }
    return docs;
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

}
